/**
 * Auth do USUÁRIO (contador) — login por senha, sem Clerk/token externo.
 *
 * Espelha lib/admin-auth.ts, mas para o perfil "usuário/contador":
 * token = base64(email:senha) guardado no localStorage; o fetch global é
 * monkey-patched pra injetar `Authorization: Bearer <token>` em chamadas
 * pra `/api/v1/usuario/*` (exceto login/cadastro, que não exigem auth).
 *
 * Backend valida contra a tabela `contadores` (services/contadores.py).
 */
const STORAGE_KEY = "acordei_usuario_token";
const NOME_KEY = "acordei_usuario_nome";
const PAPEL_KEY = "acordei_usuario_papel";
const EMAIL_KEY = "acordei_usuario_email";

export function getUsuarioToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setUsuarioSessao(
  token: string, nome: string, papel: string = "contador", email?: string,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, token.trim());
    window.localStorage.setItem(NOME_KEY, nome.trim());
    window.localStorage.setItem(PAPEL_KEY, papel === "captador" ? "captador" : "contador");
    if (email) window.localStorage.setItem(EMAIL_KEY, email.trim().toLowerCase());
  } catch {
    /* localStorage bloqueado — noop */
  }
}

/** E-mail do usuário logado. Para login Google o token é opaco (sessão), então
 * usamos o e-mail guardado; para login por senha, decodificamos base64(email:senha).
 * Vincula os casos enviados ao contador/captador (detalhes.contador_email). */
export function getUsuarioEmail(): string | null {
  if (typeof window !== "undefined") {
    try {
      const salvo = window.localStorage.getItem(EMAIL_KEY);
      if (salvo) return salvo;
    } catch {
      /* segue pro fallback do token */
    }
  }
  const token = getUsuarioToken();
  if (!token) return null;
  try {
    const decoded = atob(token);
    const i = decoded.indexOf(":");
    return i > 0 ? decoded.slice(0, i).trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

/** Papel do usuário logado (contador | captador) — usado para marcar o modelo do caso. */
export function getUsuarioPapel(): "contador" | "captador" {
  if (typeof window === "undefined") return "contador";
  try {
    return window.localStorage.getItem(PAPEL_KEY) === "captador" ? "captador" : "contador";
  } catch {
    return "contador";
  }
}

export function getUsuarioNome(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(NOME_KEY);
  } catch {
    return null;
  }
}

export function clearUsuarioSessao(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(NOME_KEY);
    window.localStorage.removeItem(PAPEL_KEY);
    window.localStorage.removeItem(EMAIL_KEY);
  } catch {
    /* noop */
  }
}

/** Captura o token de sessão do FRAGMENTO da URL após o login Google
 * (`#contador_token=…`), guarda e limpa o hash. Retorna true se logou agora.
 * Chamar no carregamento do painel. nome/e-mail reais vêm depois do /dashboard. */
export function capturarTokenOAuth(): boolean {
  if (typeof window === "undefined") return false;
  const hash = window.location.hash || "";
  const m = hash.match(/contador_token=([^&]+)/);
  if (!m) return false;
  const token = decodeURIComponent(m[1]);
  if (!token) return false;
  setUsuarioSessao(token, getUsuarioNome() || "Contador", getUsuarioPapel());
  try {
    history.replaceState(null, "", window.location.pathname + window.location.search);
  } catch {
    /* noop */
  }
  return true;
}

export function isUsuarioLoggedIn(): boolean {
  return !!getUsuarioToken();
}

let patchInstalado = false;

/** Injeta o Bearer do usuário em `/api/v1/usuario/*` (same-origin). */
export function installUsuarioFetchPatch(): void {
  if (patchInstalado || typeof window === "undefined") return;
  patchInstalado = true;

  const origFetch = window.fetch.bind(window);

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    let url: string;
    if (typeof input === "string") url = input;
    else if (input instanceof URL) url = input.toString();
    else url = input.url;

    // Só rotas de usuário autenticadas — login/cadastro não levam token.
    const ehUsuario =
      url.includes("/api/v1/usuario/") &&
      !url.includes("/usuario/login") &&
      !url.includes("/usuario/cadastro");

    if (ehUsuario) {
      let sameOrigin = false;
      if (url.startsWith("/")) {
        sameOrigin = true;
      } else {
        try {
          sameOrigin = new URL(url).origin === window.location.origin;
        } catch {
          sameOrigin = false;
        }
      }
      if (sameOrigin) {
        const token = getUsuarioToken();
        if (token) {
          const headers = new Headers(init?.headers || {});
          if (!headers.has("Authorization")) {
            headers.set("Authorization", `Bearer ${token}`);
          }
          init = { ...init, headers };
        }
      }
    }

    return origFetch(input as RequestInfo, init);
  };
}
